import {
  buildClubSlug,
  buildPlayerSlug,
  matchesTiebreaker,
  parseSlugTiebreakers,
  resolvePlayerBySlug,
  resolvePlayerBySlugAlias,
  resolveTeamBySlug,
  toSlug
} from './slugs';
import { fetchPlayers, type Player } from '@api-service/players';
import { fetchTeams, type Team } from '@api-service/teams';

/**
 * Server-side data access for the /fpl/players routes. Everything here runs
 * unauthenticated (inside getStaticProps / getServerSideProps), so every
 * player fetch excludes predictions: asking for them without a token is a
 * 400, not a zeroed payload.
 */

const PAGE_LIMIT = 100;
/** A squad is ~35 rows; a whole league ~800. Caps bound a runaway cursor. */
const MAX_SQUAD_PAGES = 5;
const MAX_LEAGUE_PAGES = 30;
/** Wide enough for every league-wide namesake of one searched display name. */
const SEARCH_CANDIDATE_LIMIT = 25;

const fetchAllPages = async (
  options: Parameters<typeof fetchPlayers>[0],
  maxPages: number
): Promise<Player[]> => {
  const rows: Player[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const response = await fetchPlayers({
      ...options,
      limit: PAGE_LIMIT,
      exclude: 'predictions',
      ...(cursor ? { cursor } : {})
    });
    rows.push(...response.data);

    if (!response.meta?.hasMore || !response.meta.nextCursor) break;
    cursor = response.meta.nextCursor;
  }

  return rows;
};

/** Ownership-sorted so index pages lead with the players people search for. */
export const fetchClubSquad = (teamId: string): Promise<Player[]> =>
  fetchAllPages(
    { teamId, sortBy: 'ownership', sortDirection: 'desc' },
    MAX_SQUAD_PAGES
  );

export const fetchLeaguePlayers = (): Promise<Player[]> =>
  fetchAllPages(
    { sortBy: 'ownership', sortDirection: 'desc' },
    MAX_LEAGUE_PAGES
  );

export type ClubEntry = {
  team: Team;
  clubSlug: string;
};

export const fetchClubs = async (): Promise<ClubEntry[]> => {
  const teams = await fetchTeams();
  return teams
    .map((team) => ({ team, clubSlug: buildClubSlug(team) }))
    .sort((first, second) => first.team.name.localeCompare(second.team.name));
};

export type ResolvedClub = {
  team: Team;
  clubSlug: string;
  squad: Player[];
};

export const resolveClub = async (
  clubSlug: string
): Promise<ResolvedClub | null> => {
  const teams = await fetchTeams();
  const team = resolveTeamBySlug(teams, clubSlug);
  if (!team) return null;

  const squad = await fetchClubSquad(team.id);
  return { team, clubSlug, squad };
};

export type ResolvedPlayerPage = ResolvedClub & {
  player: Player;
  /**
   * The player's CURRENT canonical slug. When the request arrived on a
   * historical alias (see resolvePlayerBySlugAlias) this differs from the
   * requested slug, and the page must redirect to it rather than render.
   */
  playerSlug: string;
};

export const resolvePlayerPage = async (
  clubSlug: string,
  playerSlug: string
): Promise<ResolvedPlayerPage | null> => {
  const club = await resolveClub(clubSlug);
  if (!club) return null;

  const player =
    resolvePlayerBySlug(club.squad, playerSlug) ??
    resolvePlayerBySlugAlias(club.squad, playerSlug);
  if (!player) return null;

  return { ...club, player, playerSlug: buildPlayerSlug(player, club.squad) };
};

export type CanonicalPlayerLocation = {
  clubSlug: string;
  playerSlug: string;
};

/**
 * The canonical slug URL for a player known only by their cross-season FPL
 * code. This is the identifier the retired legacy hub used in its
 * `/player-profile/<fplCode>` URLs, which hold years of accumulated search
 * equity; the players list cannot filter by it, so the league is scanned.
 * Callers cache the result via ISR, so the scan runs at most once per code
 * per revalidation window.
 */
export const locatePlayerByFplCode = async (
  fplCode: number
): Promise<CanonicalPlayerLocation | null> => {
  const leaguePlayers = await fetchLeaguePlayers();
  const player = leaguePlayers.find(
    (candidate) => candidate.externalIds.fplCode === fplCode
  );
  if (!player) return null;

  return locatePlayerByOptaId(player.externalIds.optaUuid);
};

/**
 * Cross-club recovery for a player slug whose club segment no longer
 * matches: the player transferred, the old club was relegated out of the
 * teams list, or an inbound link simply named the wrong club. The slug is
 * turned back into search text and the league is asked; the match must be
 * exact on the slugified display name (and on the collision tiebreaker when
 * the slug carries one) and unique, so fuzzy search results can never
 * relocate a URL to the wrong player. Callers redirect to the returned
 * canonical location.
 */
export const locatePlayerBySlugSearch = async (
  playerSlug: string
): Promise<CanonicalPlayerLocation | null> => {
  // The whole slug as a name is tried first, so a real name that merely
  // looks suffixed never loses its tail; the tiebreaker readings then cover
  // both suffix forms buildPlayerSlug can emit.
  const interpretations: {
    baseSlug: string;
    isMatch: (candidate: Player) => boolean;
  }[] = [
    { baseSlug: playerSlug, isMatch: () => true },
    ...parseSlugTiebreakers(playerSlug).map((tiebreaker) => ({
      baseSlug: tiebreaker.baseSlug,
      isMatch: (candidate: Player) => matchesTiebreaker(candidate, tiebreaker)
    }))
  ];

  for (const { baseSlug, isMatch } of interpretations) {
    const response = await fetchPlayers({
      search: baseSlug.replace(/-/g, ' '),
      limit: SEARCH_CANDIDATE_LIMIT,
      exclude: 'predictions'
    });

    const candidates = response.data.filter(
      (candidate) =>
        toSlug(candidate.displayName) === baseSlug && isMatch(candidate)
    );
    if (candidates.length === 1) {
      return locatePlayerByOptaId(candidates[0].externalIds.optaUuid);
    }
  }

  return null;
};

/**
 * The canonical location of one member of an already-fetched squad. Article
 * block hydration shares it so the club and player slug rules live in one
 * place rather than drifting between the profile routes and the CMS renderer.
 */
export const locateWithinSquad = (
  team: Team,
  squad: Player[],
  optaId: string
): CanonicalPlayerLocation | null => {
  const squadRow = squad.find(
    (candidate) => candidate.externalIds.optaUuid === optaId
  );
  if (!squadRow) return null;

  return {
    clubSlug: buildClubSlug(team),
    playerSlug: buildPlayerSlug(squadRow, squad)
  };
};

/**
 * The canonical slug URL for a player known only by Opta UUID. The squad is
 * fetched as well because the slug is collision-aware: it cannot be derived
 * from the player row alone.
 */
export const locatePlayerByOptaId = async (
  optaId: string
): Promise<CanonicalPlayerLocation | null> => {
  const [playerResponse, teams] = await Promise.all([
    fetchPlayers({ optaIds: optaId, limit: 1, exclude: 'predictions' }),
    fetchTeams()
  ]);

  const player = playerResponse.data[0];
  if (!player) return null;

  const team = teams.find(
    (candidate) => candidate.shortName === player.team.shortName
  );
  if (!team) return null;

  const squad = await fetchClubSquad(team.id);
  return locateWithinSquad(team, squad, optaId);
};
